import { Component, DestroyRef, inject, ViewChild, ViewContainerRef, type OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WindowService } from '@big-direkt/utils/environment';
import { type BehaviorSubject } from 'rxjs';
import { Config } from './interfaces/config';
import { Message } from './interfaces/message';
import { MessageButton } from './interfaces/message-button';
import { ChatService } from './services/chat.service';
import { MessagesService } from './services/messages.service';

@Component({
    selector: 'big-chat-core-bot',
    template: '',
    standalone: false,
})
export class ChatCoreBotComponent implements OnInit {
    private readonly handoverTimeout = 3000;
    private readonly windowService = inject(WindowService);

    protected readonly chatService = inject(ChatService);
    protected readonly messagesService = inject(MessagesService);
    protected readonly destroyRef = inject(DestroyRef);

    public readonly handover = output<boolean>();
    @ViewChild('messageWrapper', { static: true, read: ViewContainerRef }) public messageWrapper!: ViewContainerRef;

    public config!: BehaviorSubject<Config>;
    public messages: Message[] = [];

    private hiddenHandoverIsCalled = false;

    public ngOnInit(): void {
        this.config = this.chatService.getConfig();
        this.messages = this.messagesService.getAll();

        this.messagesService.updated.pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe((item: Message): void => {
            if (this.config.getValue().bot.targetBlank ?? false) {
                item.text = this.addTargetBlank(item.text);
            }

            if ((item.doHandover ?? false) && !this.hiddenHandoverIsCalled) {
                this.hiddenHandoverIsCalled = true;

                setTimeout((): void => {
                    this.handover.emit(true);
                }, this.handoverTimeout);
            }

            const messages: Message[] = this.messagesService.getAll();

            setTimeout((): void => {
                this.scrollToBottom();
            }, 1);

            if (messages.length !== this.messages.length) {
                this.messages = messages;
                this.scrollToBottom();
            }
        });

        if (this.messages.length) {
            return;
        }
    }

    public toggleUseNote(): void {
        this.chatService.toggleUserNote();
    }

    public scrollToBottom(): void {
        try {
            this.messageWrapper.element.nativeElement.scrollTop = this.messageWrapper.element.nativeElement.scrollHeight;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    }

    public buttonClick(button: MessageButton): void {
        if (button.payload === '/handover' || button.payload === '/_uebergabe_livechat') {
            this.handover.emit(true);

            return;
        }

        const hyperlinkMatch  = /\((https?:\/\/[^\s)]+)\)/.exec(button.payload);

        if (hyperlinkMatch) {
            this.windowService.nativeWindow()?.open(hyperlinkMatch[1], '_blank');

            return;
        }

        const message: Message = this.messagesService.createMessage(false, button.payload);
        void this.messagesService.sendMessage(message, false);
    }

    public errorButtonClick(failedMessage: Message): void {
        const message: Message = this.messagesService.createMessage(false, failedMessage.text);
        this.messagesService.remove(failedMessage);
        void this.messagesService.sendMessage(message);
    }

    private addTargetBlank(message: string): string {
        return message.replace(/href/g, 'target="_blank" href');
    }
}
